import { useQuery } from '@tanstack/react-query';
import dayjs from '~/timeInit';

import {
  DEFAULT_TRIP_PLANNER_CHAT_STATS_DAYS,
  TRIP_PLANNER_CHAT_STATS_TIMEZONE,
  buildTripPlannerChatStats,
  filterLuxuryEscapesInternalConversations,
} from '~/components/TripPlanner/components/tripPlannerChatStatsUtils';

import {
  type AdminChatConversationDetails,
  type GetChatConversationsOptions,
  getChatConversations,
} from '~/services/TripPlannerService';
import UsersService from '~/services/UsersService';

import { tripPlannerQueryKeys } from './tripPlannerQueryKeys';

const CHAT_STATS_PAGE_SIZE = 100;
const LE_BRAND = 'luxuryescapes';

function hasValidChatConversationsResponse(
  response: unknown,
): response is Awaited<ReturnType<typeof getChatConversations>> {
  const result = (response as { result?: { trips?: unknown; numberOfTrips?: unknown } })?.result;

  return Array.isArray(result?.trips) && typeof result?.numberOfTrips === 'number';
}

export interface GetChatConversationStatsParams {
  chatId?: string;
  userId?: string;
  days?: number;
  includeLuxuryEscapesUsers?: boolean;
}

async function fetchRecentChatConversations(
  { chatId, userId, days = DEFAULT_TRIP_PLANNER_CHAT_STATS_DAYS }: GetChatConversationStatsParams,
  now: Date,
  options?: GetChatConversationsOptions,
): Promise<Array<AdminChatConversationDetails>> {
  const cutoffDate = dayjs(now)
    .tz(TRIP_PLANNER_CHAT_STATS_TIMEZONE)
    .startOf('day')
    .subtract(days - 1, 'day');
  const conversations: Array<AdminChatConversationDetails> = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await getChatConversations(
      {
        page,
        pageSize: CHAT_STATS_PAGE_SIZE,
        chatId,
        userId,
      },
      options,
    );

    if (response.status !== 200 || !hasValidChatConversationsResponse(response)) {
      break;
    }

    const pageConversations = response.result.trips;
    totalPages = Math.max(1, Math.ceil(response.result.numberOfTrips / CHAT_STATS_PAGE_SIZE));

    conversations.push(
      ...pageConversations.filter(
        (conversation) => !dayjs(conversation.createdAt).tz(TRIP_PLANNER_CHAT_STATS_TIMEZONE).isBefore(cutoffDate),
      ),
    );

    if (pageConversations.length === 0) {
      break;
    }

    const oldestConversation = pageConversations[pageConversations.length - 1];

    // The admin list is newest-first, so once a page ends before the cutoff
    // we can stop fetching further pages for the 30-day stats window.
    if (dayjs(oldestConversation.createdAt).tz(TRIP_PLANNER_CHAT_STATS_TIMEZONE).isBefore(cutoffDate)) {
      break;
    }

    page += 1;
  }

  return conversations;
}

export function useGetChatConversationStats(params: GetChatConversationStatsParams = {}) {
  const { days = DEFAULT_TRIP_PLANNER_CHAT_STATS_DAYS, chatId, userId, includeLuxuryEscapesUsers = false } = params;

  return useQuery({
    queryKey: tripPlannerQueryKeys.chatConversationStats({
      days,
      chatId,
      userId,
      includeLuxuryEscapesUsers,
    }),
    queryFn: async ({ signal }) => {
      const now = new Date();
      const conversations = await fetchRecentChatConversations(
        {
          days,
          chatId,
          userId,
        },
        now,
        { signal },
      );
      const createdByIds = Array.from(
        new Set(conversations.map((conversation) => conversation.createdBy).filter(Boolean)),
      );
      const userSummariesById = createdByIds.length
        ? await UsersService.getUsersSummaryByIds(createdByIds, LE_BRAND)
        : new Map();
      const filteredConversations = includeLuxuryEscapesUsers
        ? conversations
        : filterLuxuryEscapesInternalConversations(conversations, userSummariesById);

      return buildTripPlannerChatStats(filteredConversations, days, now);
    },
    retry: false,
  });
}
